import { PrePopulatedTypeEnum } from '../api/model/pre-populated-type-enum';

export const PrePopulatedFormType = {
  Json: 'json',
  RegField: 'registration-fields',
  ExternApi: 'external-api',
  WfComp: 'workflow-component',
} as const;

export type PrePopulatedFormType =
  (typeof PrePopulatedFormType)[keyof typeof PrePopulatedFormType];
import {
  ActionResponse,
  ActionSettingTypeCodes,
  DropdownInputType,
  EventDropdownOptions,
  GeofenceDataSource,
  GeofenceShapeType,
  GeofenceTriggerType,
  InputDataType,
  InputTextType,
  OptionsActions,
  OptionsUnit,
  PopulateDropdown,
  PrePopulatedValue,
  RadioCheckboxInputType,
  SliderInputType,
  Switch,
  TreeSelectOptions,
  TriggerDropdownOptions,
  TriggerTypes,
} from '../models';

// Shared builder for the pre-populate source-type dropdown. Every input action
// (TEXT, DROPDOWN, SLIDER, RADIOCHECKBOX, TEMP) offers the same source types,
// including "Previous Workflow Component" (WFComp). workflowSourceOptions is the
// eligible previous-component tree, surfaced when that source type is selected.
//
// canUseReferenceJson=false (a global admin authoring a template, which never
// carries reference JSON) drops "JSON" from the options. canSelectFleet=false
// (same admin - a template also never has a division) drops "Registration Fields"
// too, since that source is populated per-division and would be just as
// non-functional. Both default to "Previous Workflow Component" instead, which is
// genuinely functional for a template - it references the template's own earlier
// actions, not anything division- or JSON-derived.
export const buildPrePopulateDropdowns = (
  workflowSourceOptions: TreeSelectOptions[] = [],
  canUseReferenceJson = true,
  canSelectFleet = true
): PopulateDropdown[] => {
  const optionsLeft = [
    { label: 'JSON', value: PrePopulatedFormType.Json },
    { label: 'Registration Fields', value: PrePopulatedFormType.RegField },
    {
      label: 'Previous Workflow Component',
      value: PrePopulatedFormType.WfComp,
    },
  ].filter(
    o =>
      (canUseReferenceJson || o.value !== PrePopulatedFormType.Json) &&
      (canSelectFleet || o.value !== PrePopulatedFormType.RegField)
  );

  const defaultOption =
    canUseReferenceJson && canSelectFleet
      ? optionsLeft[0]
      : (optionsLeft.find(o => o.value === PrePopulatedFormType.WfComp) ??
        optionsLeft[0]);

  return [
    {
      name: 'json',
      optionsLeft,
      selectedItemLeft: defaultOption,
      optionsRight: [{ label: 'Value', value: 'value' }],
      selectedItemRight: { label: 'Value', value: 'value' },
      readonlyLeft: false,
      readonlyRight: true,
      workflowSourceOptions,
    },
  ];
};

const getPrePopulateJsonFormData = (prePopulatedValue?: PrePopulatedValue) => {
  const type = prePopulatedValue?.type;

  if (type === PrePopulatedTypeEnum.ExternApi) {
    return {
      jsonLeft: {
        label: 'External API',
        value: PrePopulatedFormType.ExternApi,
      },
      jsonRight: prePopulatedValue?.value ?? '',
    };
  }

  if (type === PrePopulatedTypeEnum.RegField) {
    return {
      jsonLeft: {
        label: 'Registration Fields',
        value: PrePopulatedFormType.RegField,
      },
      jsonRight: prePopulatedValue?.regFieldId
        ? {
            label: prePopulatedValue?.value ?? 'Select a field',
            value: prePopulatedValue?.value ?? '',
            regFieldId: prePopulatedValue.regFieldId,
          }
        : null,
    };
  }

  if (type === PrePopulatedTypeEnum.WfComp) {
    return {
      jsonLeft: {
        label: 'Previous Workflow Component',
        value: PrePopulatedFormType.WfComp,
      },
      jsonRight: prePopulatedValue?.sourceActionRefId
        ? { data: prePopulatedValue.sourceActionRefId.toString() }
        : null,
    };
  }

  return {
    jsonLeft: { label: 'JSON', value: PrePopulatedFormType.Json },
    jsonRight: {
      label: prePopulatedValue?.value ?? 'Value',
      jsonFieldId: prePopulatedValue?.jsonFieldId ?? null,
    },
  };
};

const flatOptions = (
  data: TreeSelectOptions[] | undefined
): TreeSelectOptions[] => {
  if (!data) return [];
  return data.reduce<TreeSelectOptions[]>((result, item) => {
    if (Array.isArray(item.children)) {
      return result.concat(flatOptions(item.children));
    } else {
      result.push(item);
      return result;
    }
  }, []);
};

export const getCurrentOptions = (
  triggerType: string,
  data: TreeSelectOptions[] | undefined,
  segmentId: number | undefined
): TreeSelectOptions[] => {
  const segmentChildren = (segmentId: string) =>
    data?.find(segment => segment.data === segmentId)?.children || [];

  switch (triggerType) {
    case TriggerTypes.GEOFENCE_TRIGGER:
    case TriggerTypes.BUTTON_TRIGGER: {
      const newData = flatOptions(segmentChildren(`${segmentId}`));
      const tagFilter =
        triggerType === TriggerTypes.GEOFENCE_TRIGGER ? 'GEOFENCE' : 'BUTTON';
      return newData.filter(a => a.tag === tagFilter);
    }
    case TriggerTypes.STEP_COMPLETED: {
      const steps = segmentChildren(`${segmentId}`).map(step => ({
        ...step,
        children: [],
        selectable: true,
      }));
      return steps;
    }
    default: {
      const segments = data?.map(segment => ({
        ...segment,
        children: [],
        selectable: true,
      }));
      return segments || [];
    }
  }
};

export const parseSwitches = (switches?: Switch[]) => {
  return switches?.reduce(
    (obj: Record<string, boolean>, item) => (
      (obj[item.name] = item.checked ?? false), obj
    ),
    {} as Record<string, boolean>
  );
};

export const actionSettingsBarcodeScanner = (
  actionResponse?: ActionResponse
) => {
  const barcodesRaw = actionResponse?.properties?.['barcodes'];
  return {
    label: actionResponse?.properties?.['label'] ?? '',
    barcodes: barcodesRaw ?? '[]',
  };
};

export const actionSettingsButton = (
  actionResponse?: ActionResponse,
  data?: TreeSelectOptions[],
  segmentId?: number
) => {
  const actionIds = actionResponse?.properties?.['actionIds'];
  const actionIdsParsed = actionIds ? JSON.parse(actionIds) : [];
  const currentOptions = getCurrentOptions(
    TriggerTypes.BUTTON_TRIGGER,
    data,
    segmentId
  );
  const optionFound = currentOptions.find(o => o.data === actionIdsParsed[0]);
  const formData = getPrePopulateJsonFormData(
    actionResponse?.prePopulatedValues?.[0]
  );

  const isEmailOrSmsSubtype =
    actionResponse?.properties?.['action'] === 'EMAILAPP' ||
    actionResponse?.properties?.['action'] === 'SMSAPP';

  const recipientJsonFieldId = isEmailOrSmsSubtype
    ? Number(actionResponse?.properties?.['prePopulateValue']) || undefined
    : undefined;

  const recipientFormData = getPrePopulateJsonFormData(
    recipientJsonFieldId
      ? ({
          type: PrePopulatedTypeEnum.Json,
          jsonFieldId: recipientJsonFieldId,
          value: actionResponse?.properties?.['prePopulateLabel'] ?? null,
        } as PrePopulatedValue)
      : undefined
  );

  return {
    label: actionResponse?.properties?.['label'] ?? '',
    action: actionResponse?.properties?.['action']
      ? OptionsActions.find(
          o => o.value === actionResponse?.properties?.['action']
        )
      : actionResponse?.type === ActionSettingTypeCodes.HYPERLINK ||
          actionResponse?.type === ActionSettingTypeCodes.WEBFORM
        ? OptionsActions.find(o => o.value === 'WEBVIEW')
        : null,
    link: actionResponse?.properties?.['link'] ?? '',
    isPredefined: actionResponse?.properties?.['isPredefined'] === 'true',
    phoneNumber: actionResponse?.properties?.['phoneNumber'] ?? '',
    prepopulate: {
      isPrePopulated: actionResponse?.isPrePopulated ?? false,
      json: formData,
    },
    recipientPrepopulate: {
      isPrePopulated: isEmailOrSmsSubtype
        ? actionResponse?.properties?.['prePopulateRecipient'] === 'true'
        : false,
      json: recipientFormData,
    },
    json: formData,
    subject: actionResponse?.properties?.['subject'] ?? '',
    actionIds: optionFound ?? null,
    bodyMessage: actionResponse?.properties?.['bodyMessage'] ?? '',
    scanDivisionId: actionResponse?.properties?.['scanDivisionId']
      ? {
          label: actionResponse.properties['scanDivisionId']!,
          value: actionResponse.properties['scanDivisionId']!,
        }
      : null,
    scanParams: (() => {
      try {
        return actionResponse?.properties?.['scanParams']
          ? JSON.parse(actionResponse.properties['scanParams']!)
          : [];
      } catch {
        return [];
      }
    })(),
  };
};

const DATETIME_TIME_TYPES = ['TIME', 'TIMERANGE', 'AMOUNTTIME'];
const DATETIME_DATE_TYPES = ['DATE', 'DATERANGE'];
const DATETIME_COMBINED_TYPES = ['DTTM', 'DTTMRANGE'];

export const serializeDateTimeFormat = (
  type: string,
  dateFormat: string,
  timeFormat: string
): string => {
  if (DATETIME_COMBINED_TYPES.includes(type)) {
    return `date:${dateFormat} time:${timeFormat}`;
  }
  if (DATETIME_DATE_TYPES.includes(type)) {
    return dateFormat;
  }
  if (DATETIME_TIME_TYPES.includes(type)) {
    return timeFormat;
  }
  return '';
};

export const parseDateTimeFormat = (
  type: string,
  format: string
): { dateFormat: string; timeFormat: string } => {
  if (!format) return { dateFormat: '', timeFormat: '' };
  if (DATETIME_COMBINED_TYPES.includes(type)) {
    const sep = ' time:';
    const sepIdx = format.indexOf(sep);
    if (format.startsWith('date:') && sepIdx > -1) {
      return {
        dateFormat: format.slice('date:'.length, sepIdx),
        timeFormat: format.slice(sepIdx + sep.length),
      };
    }
    return { dateFormat: '', timeFormat: '' };
  }
  if (DATETIME_DATE_TYPES.includes(type)) {
    return { dateFormat: format, timeFormat: '' };
  }
  if (DATETIME_TIME_TYPES.includes(type)) {
    return { dateFormat: '', timeFormat: format };
  }
  return { dateFormat: '', timeFormat: '' };
};

export const actionSettingsDatetime = (
  switches?: Switch[],
  actionResponse?: ActionResponse
) => {
  const ppv0 = actionResponse?.prePopulatedValues?.[0];
  const ppv1 = actionResponse?.prePopulatedValues?.[1];
  const formData0 = getPrePopulateJsonFormData(ppv0);
  const formData1 = getPrePopulateJsonFormData(ppv1);
  const typeStr = actionResponse?.properties?.['type'] ?? '';
  const formatStr = actionResponse?.properties?.['format'] ?? '';
  const { dateFormat: parsedDate, timeFormat: parsedTime } =
    parseDateTimeFormat(typeStr, formatStr);
  return {
    label: actionResponse?.properties?.['label'] ?? '',
    type: typeStr,
    timeFormat: parsedTime,
    dateFormat: parsedDate,
    PREPOPULATE_TIME: {
      isPrePopulated: actionResponse?.isPrePopulated ?? false,
      json1: {
        json1Left: formData0.jsonLeft,
        json1Right: formData0.jsonRight,
      },
      ...parseSwitches(switches),
    },
    PREPOPULATE_AMOUNTTIME: {
      isPrePopulated: actionResponse?.isPrePopulated ?? false,
      json1: {
        json1Left: formData0.jsonLeft,
        json1Right: formData0.jsonRight,
      },
      ...parseSwitches(switches),
    },
    PREPOPULATE_TIMERANGE: {
      isPrePopulated: actionResponse?.isPrePopulated ?? false,
      json1: {
        json1Left: formData0.jsonLeft,
        json1Right: formData0.jsonRight,
      },
      json2: {
        json2Left: formData1.jsonLeft,
        json2Right: formData1.jsonRight,
      },
      ...parseSwitches(switches),
    },

    PREPOPULATE_DATE: {
      isPrePopulated: actionResponse?.isPrePopulated ?? false,
      json1: {
        json1Left: formData0.jsonLeft,
        json1Right: formData0.jsonRight,
      },
      ...parseSwitches(switches),
    },
    PREPOPULATE_DATERANGE: {
      isPrePopulated: actionResponse?.isPrePopulated ?? false,
      json1: {
        json1Left: formData0.jsonLeft,
        json1Right: formData0.jsonRight,
      },
      json2: {
        json2Left: formData1.jsonLeft,
        json2Right: formData1.jsonRight,
      },
      ...parseSwitches(switches),
    },
    PREPOPULATE_DTTM: {
      isPrePopulated: actionResponse?.isPrePopulated ?? false,
      json1: {
        json1Left: formData0.jsonLeft,
        json1Right: formData0.jsonRight,
      },
      ...parseSwitches(switches),
    },
    PREPOPULATE_DTTMRANGE: {
      isPrePopulated: actionResponse?.isPrePopulated ?? false,
      json1: {
        json1Left: formData0.jsonLeft,
        json1Right: formData0.jsonRight,
      },
      json2: {
        json2Left: formData1.jsonLeft,
        json2Right: formData1.jsonRight,
      },
      ...parseSwitches(switches),
    },
  };
};

export const actionSettingsDropdownSelectRadioCheckbox = (
  switches?: Switch[],
  actionResponse?: ActionResponse,
  dropdown?: boolean
) => {
  let choices = [''];
  if (actionResponse?.properties?.['choices']) {
    const raw = actionResponse.properties['choices'];
    try {
      choices = JSON.parse(raw);
    } catch {
      try {
        choices = JSON.parse(raw.replace(/'/g, '"'));
      } catch {
        choices = [''];
      }
    }
  }
  const formData = getPrePopulateJsonFormData(
    actionResponse?.prePopulatedValues?.[0]
  );
  return {
    label: actionResponse?.properties?.['label'] ?? '',
    prepopulate: {
      isPrePopulated: actionResponse?.isPrePopulated ?? false,
      json: formData,
      ...parseSwitches(switches),
    },
    type: actionResponse?.properties?.['type']
      ? actionResponse?.properties?.['type']
      : dropdown
        ? DropdownInputType.DDSINGLE
        : RadioCheckboxInputType.CBSINGLE,
    choices,
  };
};

export const actionSettingsSliderScale = (
  switches?: Switch[],
  actionResponse?: ActionResponse
) => {
  const formData = getPrePopulateJsonFormData(
    actionResponse?.prePopulatedValues?.[0]
  );
  return {
    label: actionResponse?.properties?.['label'] ?? '',
    labelLeft: actionResponse?.properties?.['labelLeft'] ?? '',
    labelRight: actionResponse?.properties?.['labelRight'] ?? '',
    prepopulate: {
      isPrePopulated: actionResponse?.isPrePopulated ?? false,
      json: formData,
      ...parseSwitches(switches),
    },
    type: actionResponse?.properties?.['type'] ?? SliderInputType.NUMERIC,
    minAmnt: actionResponse?.properties?.['minAmnt'] ?? '',
    maxAmnt: actionResponse?.properties?.['maxAmnt'] ?? '',
  };
};

export const actionSettingsTextInput = (
  switches?: Switch[],
  actionResponse?: ActionResponse
) => {
  const isMinLenStr = actionResponse?.properties?.['isMinLen'];
  const isMinLen = isMinLenStr === 'true';
  const isMaxLenStr = actionResponse?.properties?.['isMaxLen'];
  const isMaxLen = isMaxLenStr === 'true';
  const formData = getPrePopulateJsonFormData(
    actionResponse?.prePopulatedValues?.[0]
  );
  return {
    label: actionResponse?.properties?.['label'] ?? '',
    prepopulate: {
      isPrePopulated: actionResponse?.isPrePopulated ?? false,
      json: formData,
      ...parseSwitches(switches),
    },
    type: actionResponse?.properties?.['type'] ?? InputTextType.SHORT,
    dataType: actionResponse?.properties?.['dataType']
      ? actionResponse?.properties?.['dataType']
      : InputDataType.ALL,
    isMinLen,
    isMaxLen,
    minLen: isMinLen ? (actionResponse?.properties?.['minLen'] ?? '') : '',
    maxLen: isMaxLen ? (actionResponse?.properties?.['maxLen'] ?? '') : '',
  };
};

export const actionSettingsTimerStopwatch = (
  actionResponse?: ActionResponse,
  data?: TreeSelectOptions[],
  segmentId?: number
) => {
  const startTriggerType = actionResponse?.properties?.['startTriggerType'];
  const startTriggerId = actionResponse?.properties?.['startTriggerId'];
  const startCurrentOptions = getCurrentOptions(
    startTriggerType!,
    data,
    segmentId
  );
  const startOptionFound = startCurrentOptions.find(
    o => o.data === startTriggerId
  );

  const stopTriggerType = actionResponse?.properties?.['stopTriggerType'];
  const stopTriggerId = actionResponse?.properties?.['stopTriggerId'];
  const stopCurrentOptions = getCurrentOptions(
    stopTriggerType!,
    data,
    segmentId
  );
  const stopOptionFound = stopCurrentOptions.find(
    o => o.data === stopTriggerId
  );

  return {
    startTrigger: {
      triggerType: startTriggerType
        ? TriggerDropdownOptions.find(o => o.value === startTriggerType)
        : TriggerDropdownOptions[0],
      triggerId: startOptionFound ?? null,
    },
    stopTrigger: {
      triggerType: stopTriggerType
        ? TriggerDropdownOptions.find(o => o.value === stopTriggerType)
        : TriggerDropdownOptions[0],
      triggerId: stopOptionFound ?? null,
    },
  };
};

export const actionSettingsTFLO = (
  actionResponse?: ActionResponse,
  data?: TreeSelectOptions[],
  segmentId?: number
) => {
  const actionIds = actionResponse?.properties?.['actionIds'];
  const actionIdsParsed = actionIds ? JSON.parse(actionIds) : [];
  const currentOptions = getCurrentOptions(
    TriggerTypes.BUTTON_TRIGGER,
    data,
    segmentId
  );
  const optionFound = currentOptions.find(o => o.data === actionIdsParsed[0]);
  const formData = getPrePopulateJsonFormData(
    actionResponse?.prePopulatedValues?.[0]
  );

  return {
    ...actionSettingsTriggerDropdown(actionResponse, data, segmentId),
    prepopulate: {
      isPrePopulated: actionResponse?.isPrePopulated ?? false,
      json: formData,
    },
    json: formData,
    subject: actionResponse?.properties?.['subject'] ?? '',
    actionIds: optionFound ?? null,
    bodyMessage: actionResponse?.properties?.['bodyMessage'] ?? '',
  };
};

export const actionSettingsTriggerDropdown = (
  actionResponse?: ActionResponse,
  data?: TreeSelectOptions[],
  segmentId?: number
) => {
  const triggerType = actionResponse?.properties?.['triggerType'];
  const triggerId = actionResponse?.properties?.['triggerId'];
  const currentOptions = getCurrentOptions(triggerType!, data, segmentId);
  const optionFound = currentOptions.find(o => o.data === triggerId);
  return {
    trigger: {
      triggerType: triggerType
        ? TriggerDropdownOptions.find(o => o.value === triggerType)
        : TriggerDropdownOptions[0],
      triggerId: optionFound ?? null,
    },
  };
};

export const FormsByActionSetting = {
  [ActionSettingTypeCodes.ACCEPT]: (
    _?: Switch[],
    actionResponse?: ActionResponse
  ) => ({
    acceptLoadLabel:
      actionResponse?.properties?.['acceptLoadLabel'] ??
      'Swipe to accept workflow',
    buttonType: actionResponse?.properties?.['buttonType'] ?? 'SIMPLE',
  }),
  [ActionSettingTypeCodes.ACCEPTLOAD]: (
    _?: Switch[],
    actionResponse?: ActionResponse
  ) => ({
    acceptLoadLabel:
      actionResponse?.properties?.['acceptLoadLabel'] ??
      'Swipe to accept workflow',
    buttonType: actionResponse?.properties?.['buttonType'] ?? 'SIMPLE',
  }),
  [ActionSettingTypeCodes.REJECT]: () => ({}),
  [ActionSettingTypeCodes.DECLINE]: (
    _?: Switch[],
    actionResponse?: ActionResponse
  ) => ({
    declineLoadLabel:
      actionResponse?.properties?.['declineLoadLabel'] ??
      'Swipe to decline workflow',
    buttonType: actionResponse?.properties?.['buttonType'] ?? 'SIMPLE',
  }),
  [ActionSettingTypeCodes.DECLINELOAD]: (
    _?: Switch[],
    actionResponse?: ActionResponse
  ) => ({
    declineLoadLabel:
      actionResponse?.properties?.['declineLoadLabel'] ??
      'Swipe to decline workflow',
    buttonType: actionResponse?.properties?.['buttonType'] ?? 'SIMPLE',
  }),
  [ActionSettingTypeCodes.START]: (
    _?: Switch[],
    actionResponse?: ActionResponse
  ) => ({
    startLoadLabel:
      actionResponse?.properties?.['startLoadLabel'] ??
      'Swipe to start workflow',
    buttonType: actionResponse?.properties?.['buttonType'] ?? 'RIGTOLEF',
  }),
  [ActionSettingTypeCodes.STARTLOAD]: (
    _?: Switch[],
    actionResponse?: ActionResponse
  ) => ({
    startLoadLabel:
      actionResponse?.properties?.['startLoadLabel'] ??
      'Swipe to start workflow',
    buttonType: actionResponse?.properties?.['buttonType'] ?? 'RIGTOLEF',
  }),
  [ActionSettingTypeCodes.STOP]: (
    _?: Switch[],
    actionResponse?: ActionResponse,
    data?: TreeSelectOptions[],
    segmentId?: number
  ) => ({
    ...actionSettingsTriggerDropdown(actionResponse, data, segmentId),
    event: actionResponse?.properties?.['event']
      ? EventDropdownOptions.find(
          o => o.value === actionResponse?.properties?.['event']
        )
      : null,
    promptDriver: actionResponse?.properties?.['promptDriver'] === 'true',
  }),
  [ActionSettingTypeCodes.SMS]: (
    _?: Switch[],
    actionResponse?: ActionResponse,
    data?: TreeSelectOptions[],
    segmentId?: number
  ) => ({ ...actionSettingsButton(actionResponse, data, segmentId) }),
  [ActionSettingTypeCodes.PHONE]: (
    _?: Switch[],
    actionResponse?: ActionResponse,
    data?: TreeSelectOptions[],
    segmentId?: number
  ) => ({ ...actionSettingsButton(actionResponse, data, segmentId) }),
  [ActionSettingTypeCodes.BARSCAN]: (
    _?: Switch[],
    actionResponse?: ActionResponse
  ) => ({
    ...actionSettingsBarcodeScanner(actionResponse),
  }),
  [ActionSettingTypeCodes.GEOFENCE]: (
    _?: Switch[],
    actionResponse?: ActionResponse
  ) => ({
    geofenceShapeType:
      actionResponse?.properties?.['geofenceShapeType'] ||
      GeofenceShapeType.CENTROID,
    type: actionResponse?.properties?.['type'] || GeofenceTriggerType.ENTER,
    radius: actionResponse?.properties?.['radius'] ?? '',
    unit: actionResponse?.properties?.['unit']
      ? OptionsUnit.find(o => o.value === actionResponse?.properties?.['unit'])
      : null,
    source: actionResponse?.properties?.['source'] || GeofenceDataSource.STATIC,
    latitude: actionResponse?.properties?.['latitude'] ?? null,
    longitude: actionResponse?.properties?.['longitude'] ?? null,
    latJsonFieldId: {
      label: actionResponse?.properties?.['latJsonFieldLabel'] ?? null,
      jsonFieldId: actionResponse?.properties?.['latJsonFieldId'] ?? null,
    },
    longJsonFieldId: {
      label: actionResponse?.properties?.['longJsonFieldLabel'] ?? null,
      jsonFieldId: actionResponse?.properties?.['longJsonFieldId'] ?? null,
    },
    zoneSource:
      actionResponse?.properties?.['zoneSource'] || GeofenceDataSource.STATIC,
    zoneJsonFieldId: {
      label: actionResponse?.properties?.['zoneJsonFieldLabel'] ?? null,
      jsonFieldId: actionResponse?.properties?.['zoneJsonFieldId'] ?? null,
    },
    // statusMessage: '',
    notificationTitle: actionResponse?.properties?.['notificationTitle'] ?? '',
    notificationBody: actionResponse?.properties?.['notificationBody'] ?? '',
    // Must default to '' (never null): the properties save diff skips null and
    // only sends '' when the key already exists, which is how clearing works.
    timestampFormat: actionResponse?.properties?.['timestampFormat'] ?? '',
  }),
  [ActionSettingTypeCodes.PUSH]: (
    _?: Switch[],
    actionResponse?: ActionResponse,
    data?: TreeSelectOptions[],
    segmentId?: number
  ) => ({
    message: actionResponse?.properties?.['message'] ?? '',
    ...actionSettingsTriggerDropdown(actionResponse, data, segmentId),
  }),
  [ActionSettingTypeCodes.TFLO_EMAIL]: (
    _?: Switch[],
    actionResponse?: ActionResponse,
    data?: TreeSelectOptions[],
    segmentId?: number
  ) => ({ ...actionSettingsTFLO(actionResponse, data, segmentId) }),
  [ActionSettingTypeCodes.TFLO_SMS]: (
    _?: Switch[],
    actionResponse?: ActionResponse,
    data?: TreeSelectOptions[],
    segmentId?: number
  ) => ({ ...actionSettingsTFLO(actionResponse, data, segmentId) }),
  [ActionSettingTypeCodes.DEVICE_LOCATION]: (
    _?: Switch[],
    actionResponse?: ActionResponse,
    data?: TreeSelectOptions[],
    segmentId?: number
  ) => ({
    ...actionSettingsTriggerDropdown(actionResponse, data, segmentId),
  }),
  [ActionSettingTypeCodes.ELD_LOCATION]: (
    _?: Switch[],
    actionResponse?: ActionResponse,
    data?: TreeSelectOptions[],
    segmentId?: number
  ) => ({
    ...actionSettingsTriggerDropdown(actionResponse, data, segmentId),
  }),
  [ActionSettingTypeCodes.TEXT]: (
    switches?: Switch[],
    actionResponse?: ActionResponse
  ) => ({
    ...actionSettingsTextInput(switches, actionResponse),
  }),
  [ActionSettingTypeCodes.HEADING_TEXT]: (
    _?: Switch[],
    actionResponse?: ActionResponse
  ) => ({ text: actionResponse?.properties?.['text'] ?? '' }),
  [ActionSettingTypeCodes.DROPDOWN_SELECT]: (
    switches?: Switch[],
    actionResponse?: ActionResponse
  ) => ({
    ...actionSettingsDropdownSelectRadioCheckbox(
      switches,
      actionResponse,
      true
    ),
  }),
  [ActionSettingTypeCodes.LABEL]: (
    _?: Switch[],
    actionResponse?: ActionResponse
  ) => ({ label: actionResponse?.properties?.['label'] ?? '' }),
  [ActionSettingTypeCodes.RADIOCHECKBOX]: (
    switches?: Switch[],
    actionResponse?: ActionResponse
  ) => ({
    ...actionSettingsDropdownSelectRadioCheckbox(switches, actionResponse),
  }),
  [ActionSettingTypeCodes.SIMPLE_BUTTON]: (
    _?: Switch[],
    actionResponse?: ActionResponse,
    data?: TreeSelectOptions[],
    segmentId?: number
  ) => ({ ...actionSettingsButton(actionResponse, data, segmentId) }),
  [ActionSettingTypeCodes.SIDE_BY_SIDE_BUTTONS]: (
    switches?: Switch[],
    actionResponse?: ActionResponse,
    data?: TreeSelectOptions[],
    segmentId?: number
  ) => ({
    button1: {
      ...FormsByActionSetting[ActionSettingTypeCodes.SIMPLE_BUTTON](
        switches,
        {
          ...actionResponse,
          properties: {
            action: actionResponse?.properties?.['action1'] ?? '',
            label: actionResponse?.properties?.['label1'] ?? '',
            link: actionResponse?.properties?.['link1'] ?? '',
            phoneNumber: actionResponse?.properties?.['number1'] ?? '',
            isPredefined: actionResponse?.properties?.['isPredefined1'] ?? '',
            subject: actionResponse?.properties?.['subject1'] ?? '',
            bodyMessage: actionResponse?.properties?.['bodyMessage1'] ?? '',
            scanDivisionId:
              actionResponse?.properties?.['scanDivisionId1'] ?? '',
            scanParams: actionResponse?.properties?.['scanParams1'] ?? '',
            actionIds: actionResponse?.properties?.['actionIds1'] ?? '',
            prePopulateRecipient:
              actionResponse?.properties?.['prePopulateRecipient1'] ?? '',
            prePopulateValue:
              actionResponse?.properties?.['prePopulateValue1'] ?? '',
            prePopulateLabel:
              actionResponse?.properties?.['prePopulateLabel1'] ?? '',
          },
        },
        data,
        segmentId
      ),
    },
    button2: {
      ...FormsByActionSetting[ActionSettingTypeCodes.SIMPLE_BUTTON](
        switches,
        {
          ...actionResponse,
          properties: {
            action: actionResponse?.properties?.['action2'] ?? '',
            label: actionResponse?.properties?.['label2'] ?? '',
            link: actionResponse?.properties?.['link2'] ?? '',
            phoneNumber: actionResponse?.properties?.['number2'] ?? '',
            isPredefined: actionResponse?.properties?.['isPredefined2'] ?? '',
            subject: actionResponse?.properties?.['subject2'] ?? '',
            bodyMessage: actionResponse?.properties?.['bodyMessage2'] ?? '',
            scanDivisionId:
              actionResponse?.properties?.['scanDivisionId2'] ?? '',
            scanParams: actionResponse?.properties?.['scanParams2'] ?? '',
            actionIds: actionResponse?.properties?.['actionIds2'] ?? '',
            prePopulateRecipient:
              actionResponse?.properties?.['prePopulateRecipient2'] ?? '',
            prePopulateValue:
              actionResponse?.properties?.['prePopulateValue2'] ?? '',
            prePopulateLabel:
              actionResponse?.properties?.['prePopulateLabel2'] ?? '',
          },
        },
        data,
        segmentId
      ),
    },
  }),
  [ActionSettingTypeCodes.TIMER_STOPWATCH]: (
    _?: Switch[],
    actionResponse?: ActionResponse,
    data?: TreeSelectOptions[],
    segmentId?: number
  ) => ({
    ...actionSettingsTimerStopwatch(actionResponse, data, segmentId),
  }),
  [ActionSettingTypeCodes.DATETIME]: (
    switches?: Switch[],
    actionResponse?: ActionResponse
  ) => ({
    ...actionSettingsDatetime(switches, actionResponse),
  }),
  [ActionSettingTypeCodes.TEMP]: (
    switches?: Switch[],
    actionResponse?: ActionResponse
  ) => {
    const formData = getPrePopulateJsonFormData(
      actionResponse?.prePopulatedValues?.[0]
    );
    return {
      label: actionResponse?.properties?.['label'] ?? '',
      minAmnt: actionResponse?.properties?.['minAmnt'] ?? '',
      maxAmnt: actionResponse?.properties?.['maxAmnt'] ?? '',
      prepopulate: {
        isPrePopulated: actionResponse?.isPrePopulated ?? false,
        json: formData,
        ...parseSwitches(switches),
      },
    };
  },
  [ActionSettingTypeCodes.SLIDER]: (
    switches?: Switch[],
    actionResponse?: ActionResponse
  ) => ({
    ...actionSettingsSliderScale(switches, actionResponse),
  }),
  [ActionSettingTypeCodes.HYPERLINK]: (
    _?: Switch[],
    actionResponse?: ActionResponse
  ) => ({
    label: actionResponse?.properties?.['label'] ?? '',
    link: actionResponse?.properties?.['link'] ?? '',
    prepopulate: {
      isPrePopulated: actionResponse?.isPrePopulated ?? false,
      json: getPrePopulateJsonFormData(actionResponse?.prePopulatedValues?.[0]),
    },
  }),
  [ActionSettingTypeCodes.WEBFORM]: (
    _?: Switch[],
    actionResponse?: ActionResponse
  ) => ({
    label: actionResponse?.properties?.['label'] ?? '',
    link: actionResponse?.properties?.['link'] ?? '',
    prepopulate: {
      isPrePopulated: actionResponse?.isPrePopulated ?? false,
      json: getPrePopulateJsonFormData(actionResponse?.prePopulatedValues?.[0]),
    },
  }),
  [ActionSettingTypeCodes.SUBMIT_BUTTON]: (
    _?: Switch[],
    actionResponse?: ActionResponse
  ) => ({
    label: actionResponse?.properties?.['label'] ?? '',
  }),
  [ActionSettingTypeCodes.CERTIFY_LOGS]: (
    _?: Switch[],
    actionResponse?: ActionResponse
  ) => ({
    label: actionResponse?.properties?.['label'] ?? '',
    body: actionResponse?.properties?.['body'] ?? '',
    type: actionResponse?.properties?.['type'] ?? '',
  }),
  [ActionSettingTypeCodes.COMMIT_OPTIONS]: (
    _?: Switch[],
    actionResponse?: ActionResponse
  ) => ({
    acceptLabel: actionResponse?.properties?.['acceptLabel'] ?? '',
    isAcceptEnabled:
      actionResponse?.properties?.['isAcceptEnabled'] !== 'false',
    declineLabel: actionResponse?.properties?.['declineLabel'] ?? '',
    isDeclineEnabled:
      actionResponse?.properties?.['isDeclineEnabled'] !== 'false',
    startWFLabel: actionResponse?.properties?.['startWFLabel'] ?? '',
    isStartWFEnabled:
      actionResponse?.properties?.['isStartWFEnabled'] !== 'false',
    buttonType: actionResponse?.properties?.['buttonType'] ?? '',
  }),
};
